import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItemButton,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import { TableRestaurant } from '../ui/Icons';
import { formatPrice } from '../../utils/formatPrice';

/**
 * Left rail of the POS: open a new tab by table number, and pick among the
 * currently-open tabs. Each tab shows its running total + outstanding balance.
 */
const TableTabsPanel = ({ tabs = [], activeTabId, onSelectTab, onOpenTab, currency, opening }) => {
  const { t } = useTranslation();
  const [tableNumber, setTableNumber] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const value = tableNumber.trim();
    if (!value) return;
    onOpenTab(value);
    setTableNumber('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>
        {t('pos.tabs.title', 'Tables')}
      </Typography>

      <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          value={tableNumber}
          onChange={(e) => setTableNumber(e.target.value)}
          placeholder={t('pos.tabs.tableNumber', 'Table number')}
          inputProps={{ maxLength: 50, 'aria-label': t('pos.tabs.tableNumber', 'Table number') }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={opening || !tableNumber.trim()}
          startIcon={opening ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('pos.tabs.open', 'Open')}
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {tabs.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4, px: 2 }}>
            <TableRestaurant size={32} />
            <Typography sx={{ mt: 1, fontSize: 14 }}>
              {t('pos.tabs.empty', 'No open tabs yet. Open one to start an order.')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {tabs.map((tab) => {
              const active = tab.id === activeTabId;
              const owed = tab.financials?.owed ?? 0;
              return (
                <ListItemButton
                  key={tab.id}
                  selected={active}
                  onClick={() => onSelectTab(tab.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: '1px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 0.5,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 600 }}>
                      {t('pos.tabs.table', 'Table')} {tab.table_number}
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatPrice(tab.financials?.total ?? 0, currency)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {t('pos.tabs.ordersCount', '{{count}} orders', { count: tab.financials?.order_count ?? 0 })}
                    </Typography>
                    {owed > 0 ? (
                      <Chip size="small" color="warning" label={`${t('pos.owed', 'Owed')} ${formatPrice(owed, currency)}`} />
                    ) : (
                      <Chip size="small" color="success" label={t('pos.paid', 'Paid')} />
                    )}
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default TableTabsPanel;
